// One scheduled job: figures, request, schedule, run history and change history.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useDeleteJobMutation,
  useSchedulerEventQuery,
  useSchedulerHistoryFetcher,
} from '@/client/api/scheduler/scheduler.query';
import { JobWindow, RunRow } from '@/client/types/scheduler';
import JobForm from './JobForm';
import {
  ACTION_LABEL,
  AccessDenied,
  Card,
  CoverageNote,
  EmptyRow,
  ErrorNotice,
  Field,
  HealthBadge,
  JobOperateButtons,
  OutcomeBadge,
  PageShell,
  ReasonDialog,
  RunsChart,
  SCHEDULER_BASE,
  Th,
  JOB_WINDOWS,
  WindowTabs,
  describeTiming,
  fmtAgo,
  fmtDur,
  fmtNum,
  fmtRate,
  fmtTime,
  schedulerErrorText,
} from './schedulerUi';

const PAGE = 50;

const SchedulerJobDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const viewPerm = useHasPermission('scheduler.view');
  const operatePerm = useHasPermission('scheduler.operate');
  const managePerm = useHasPermission('scheduler.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canOperate = operatePerm.isSuperAdmin || operatePerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;

  const [window, setWindow] = useState<JobWindow>('24h');
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [extra, setExtra] = useState<RunRow[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);

  const q = useSchedulerEventQuery(id, window, canView);
  const del = useDeleteJobMutation();
  const fetchHistory = useSchedulerHistoryFetcher();
  const d = q.data;
  const job = d?.event;

  useBreadcrumbs([
    { label: 'Scheduler', href: SCHEDULER_BASE },
    { label: 'Jobs', href: `${SCHEDULER_BASE}/jobs` },
    { label: job?.title ?? 'Job' },
  ]);

  // A fresh first page (after a run or a refetch) replaces whatever was appended.
  const firstRun = d?.history.rows[0]?.id;
  useEffect(() => {
    setExtra([]);
  }, [id, window, firstRun]);

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Scheduler" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Scheduler" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  if (!d || !job) {
    return (
      <PageShell title="Job" description="">
        {q.isError ? (
          <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </PageShell>
    );
  }

  const rows = [...d.history.rows, ...extra];
  const loadMore = async () => {
    setLoadingMore(true);
    setMoreError(null);
    try {
      const r = await fetchHistory(id, rows.length, PAGE);
      setExtra((e) => [...e, ...r.rows]);
    } catch (err) {
      setMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (editing && canManage) {
    return (
      <PageShell title={`Edit ${job.title}`} description="Changes apply to future runs.">
        <JobForm job={job} onSaved={() => setEditing(false)} onCancel={() => setEditing(false)} />
      </PageShell>
    );
  }

  const s = job.stats;
  const p = job.params;
  const enabled = job.enabled === 1;
  return (
    <PageShell
      title={job.title}
      description={`${describeTiming(job.timing)}${job.notes ? `. ${job.notes}` : ''}`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`${SCHEDULER_BASE}/jobs`}
            className="mr-2 inline-flex items-center text-sm text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Jobs
          </Link>
          <HealthBadge health={job.health} />
          <span className="text-xs text-gray-500">{enabled ? 'Enabled' : 'Paused'}</span>
          {canOperate && <JobOperateButtons job={job} />}
          {canManage && (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setDeleting(true)}>
                Delete
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CoverageNote coverage={d.coverage} />
        <div className="ml-auto">
          <WindowTabs value={window} onChange={setWindow} options={JOB_WINDOWS} />
        </div>
      </div>

      {d.ack && (
        <div className="rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-600">
          Failures acknowledged by {d.ack.by} on {fmtTime(d.ack.at)}
          {d.ack.note ? `: ${d.ack.note}` : ''}
        </div>
      )}

      <Card title={`Last ${window}`}>
        {s ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 xl:grid-cols-8">
            <Field label="Runs">{fmtNum(s.runs)}</Field>
            <Field label="Success rate">{fmtRate(s.success_rate)}</Field>
            <Field label="Failures">{fmtNum(s.failures)}</Field>
            <Field label="Failures in a row">{fmtNum(s.consecutive_failures)}</Field>
            <Field label="Average">{fmtDur(s.avg_elapsed)}</Field>
            <Field label="p95">{fmtDur(s.p95_elapsed)}</Field>
            <Field label="Longest">{fmtDur(s.max_elapsed)}</Field>
            <Field label="Last run">
              {s.last_run ? (
                <>
                  <Link
                    to={`${SCHEDULER_BASE}/runs/${encodeURIComponent(s.last_run.job_id)}`}
                    className="hover:underline"
                  >
                    {fmtAgo(s.last_run.at)}
                  </Link>{' '}
                  <OutcomeBadge code={s.last_run.code} />
                </>
              ) : (
                '—'
              )}
            </Field>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No runs in this window.</p>
        )}
        {s?.last_failure && (
          <p className="mt-3 break-all text-xs text-gray-500">
            Last failure {fmtTime(s.last_failure.at)}:{' '}
            {s.last_failure.description || `code ${s.last_failure.code}`}
          </p>
        )}
        <div className="mt-4">
          <RunsChart series={d.series} window={window} />
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="Request">
          <div className="space-y-3">
            <Field label="Method and URL">
              <span className="font-mono text-xs">
                {p.method} {p.url}
              </span>
            </Field>
            <Field label="Headers">
              {p.headers.length === 0 ? (
                <span className="text-gray-400">None</span>
              ) : (
                <ul className="font-mono text-xs">
                  {p.headers.map((h) => (
                    <li key={h.name}>
                      {h.name}: {h.secret ? '********' : h.value}
                    </li>
                  ))}
                </ul>
              )}
            </Field>
            {p.data && (
              <Field label="Request body">
                <pre className="whitespace-pre-wrap font-mono text-xs">{p.data}</pre>
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Success match">{p.success_match || '—'}</Field>
              <Field label="Error match">{p.error_match || '—'}</Field>
            </div>
          </div>
        </Card>
        <Card title="Schedule and limits">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Schedule">{describeTiming(job.timing)}</Field>
            <Field label="Raw timing">
              <code className="text-xs">{JSON.stringify(job.timing)}</code>
            </Field>
            <Field label="Timezone">{job.timezone}</Field>
            <Field label="Timeout">{fmtDur(job.timeout)}</Field>
            <Field label="Retries">
              {job.retries}
              {job.retries > 0 ? `, ${fmtDur(job.retry_delay)} apart` : ''}
            </Field>
            <Field label="Max concurrent runs">{job.max_children}</Field>
            <Field label="Catch up on missed runs">{job.catch_up === 1 ? 'Yes' : 'No'}</Field>
            <Field label="Target">{job.target}</Field>
            <Field label="Category">{job.category}</Field>
            <Field label="Notes">{job.notes || '—'}</Field>
            <Field label="Created">{fmtTime(job.created)}</Field>
            <Field label="Modified">{fmtTime(job.modified)}</Field>
          </div>
        </Card>
      </div>

      <Card title="Run history" note={`${rows.length} of ${d.history.total}`} flush>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>Started</Th>
                <Th>Outcome</Th>
                <Th>Duration</Th>
                <Th>Host</Th>
                <Th>Detail</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={5}>No runs recorded</EmptyRow>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0">
                  <td className="whitespace-nowrap px-4 py-2">
                    <Link
                      to={`${SCHEDULER_BASE}/runs/${encodeURIComponent(r.id)}`}
                      className="text-blue-600 hover:underline"
                    >
                      {fmtTime(r.time_start || r.epoch)}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <OutcomeBadge code={r.code} />
                  </td>
                  <td className="px-4 py-2 text-gray-600">{fmtDur(r.elapsed)}</td>
                  <td className="px-4 py-2 text-gray-600">{r.hostname}</td>
                  <td className="max-w-md truncate px-4 py-2 text-gray-500">
                    {r.description || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(rows.length < d.history.total || !!moreError) && (
          <div className="flex items-center gap-3 border-t border-gray-100 px-4 py-3">
            {rows.length < d.history.total && (
              <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore}>
                {loadingMore && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Load more
              </Button>
            )}
            {!!moreError && (
              <span className="text-sm text-red-600">{schedulerErrorText(moreError)}</span>
            )}
          </div>
        )}
      </Card>

      <Card title="Change history" flush>
        <table className="w-full text-sm">
          <tbody>
            {d.audit.length === 0 && <EmptyRow cols={4}>No console changes recorded</EmptyRow>}
            {d.audit.map((a, i) => (
              <tr key={`${a.at}-${i}`} className="border-b border-gray-50 last:border-0">
                <td className="whitespace-nowrap px-4 py-2 text-gray-500">{fmtTime(a.at)}</td>
                <td className="px-4 py-2 text-gray-700">{a.by}</td>
                <td className="px-4 py-2 text-gray-900">{ACTION_LABEL[a.action] ?? a.action}</td>
                <td className="px-4 py-2 text-gray-600">
                  {a.reason || '—'}
                  {a.fields?.length ? (
                    <span className="text-gray-400"> ({a.fields.join(', ')})</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {deleting && (
        <ReasonDialog
          open
          onClose={() => {
            setDeleting(false);
            del.reset();
          }}
          title={`Delete ${job.title}`}
          description="This removes the job from the scheduler. Its run history is no longer reachable from here."
          confirmLabel="Delete job"
          destructive
          minReason={5}
          confirmText={job.title}
          busy={del.isPending}
          error={del.error}
          onConfirm={(reason) =>
            del.mutate(
              { id: job.id, reason },
              { onSuccess: () => navigate(`${SCHEDULER_BASE}/jobs`) },
            )
          }
        />
      )}
    </PageShell>
  );
};

export default SchedulerJobDetailPage;
